import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

// Types
export interface AgentTaskUser {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
}

export interface AgentTaskWorkspace {
  id: string;
  name: string;
}

export interface AgentTask {
  id: string;
  fk_user_id: string;
  fk_workspace_id: string | null;
  task_type: string;
  query: string;
  context: string | null;
  settings: Record<string, any> | null;
  status: string;
  progress: number | null;
  started_at: string | null;
  completed_at: string | null;
  result: Record<string, any> | null;
  artifacts: Array<{ name: string; path: string; type: string }> | null;
  error: string | null;
  input_tokens: number | null;
  output_tokens: number | null;
  model_used: string | null;
  created_at: string;
  updated_at: string;
  users: AgentTaskUser;
  workspaces: AgentTaskWorkspace | null;
  duration: number | null;
  messageCount?: number;
}

export interface AgentTaskMessage {
  id: string;
  fk_task_id: string;
  role: string;
  content: string;
  created_at: string;
}

export interface AgentTaskWithMessages extends AgentTask {
  agent_task_messages: AgentTaskMessage[];
}

export interface AgentTaskStats {
  totalTasks: number;
  recentTasks: number;
  runningTasks: number;
  completionRate: number;
  errorRate: number;
  avgDuration: number;
  byStatus: Record<string, number>;
  byType: Record<string, number>;
  byModel: Record<string, number>;
  tokens: {
    totalInput: number;
    totalOutput: number;
    avgInput: number;
    avgOutput: number;
  };
}

export interface Pagination {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface AdminAgentTasksFilters {
  status: string | null;
  taskType: string | null;
  workspaceId: string | null;
  userId: string | null;
  search: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}

export interface AdminAgentTasksState {
  tasks: AgentTask[];
  stats: AgentTaskStats | null;
  selectedTask: AgentTaskWithMessages | null;
  pagination: Pagination;
  filters: AdminAgentTasksFilters;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  lastUpdated: number | null;
}

const initialPagination: Pagination = {
  page: 1,
  pageSize: 20,
  total: 0,
  totalPages: 0,
};

const initialFilters: AdminAgentTasksFilters = {
  status: null,
  taskType: null,
  workspaceId: null,
  userId: null,
  search: '',
  sortBy: 'created_at',
  sortOrder: 'desc',
};

const initialState: AdminAgentTasksState = {
  tasks: [],
  stats: null,
  selectedTask: null,
  pagination: initialPagination,
  filters: initialFilters,
  status: 'idle',
  error: null,
  lastUpdated: null,
};

// Async thunks
export const fetchAgentTasks = createAsyncThunk(
  'adminAgentTasks/fetchTasks',
  async (params: {
    page?: number;
    pageSize?: number;
    status?: string;
    taskType?: string;
    workspaceId?: string;
    userId?: string;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
  } = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', String(params.page));
    if (params.pageSize) queryParams.append('pageSize', String(params.pageSize));
    if (params.status) queryParams.append('status', params.status);
    if (params.taskType) queryParams.append('taskType', params.taskType);
    if (params.workspaceId) queryParams.append('workspaceId', params.workspaceId);
    if (params.userId) queryParams.append('userId', params.userId);
    if (params.search) queryParams.append('search', params.search);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const response = await fetch(`/api/admin/agent-tasks?${queryParams}`);
    if (!response.ok) throw new Error('Failed to fetch agent tasks');
    return response.json();
  }
);

export const fetchAgentTaskStats = createAsyncThunk(
  'adminAgentTasks/fetchStats',
  async (params: { days?: number } = {}) => {
    const queryParams = new URLSearchParams();
    if (params.days) queryParams.append('days', String(params.days));

    const response = await fetch(`/api/admin/agent-tasks/stats?${queryParams}`);
    if (!response.ok) throw new Error('Failed to fetch agent task stats');
    return response.json();
  }
);

export const fetchAgentTaskDetails = createAsyncThunk(
  'adminAgentTasks/fetchDetails',
  async (taskId: string) => {
    const response = await fetch(`/api/admin/agent-tasks/${taskId}`);
    if (!response.ok) throw new Error('Failed to fetch task details');
    return response.json();
  }
);

export const cancelAgentTask = createAsyncThunk(
  'adminAgentTasks/cancel',
  async (taskId: string) => {
    const response = await fetch(`/api/admin/agent-tasks/${taskId}/cancel`, {
      method: 'POST',
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to cancel task');
    }
    return response.json();
  }
);

export const deleteAgentTask = createAsyncThunk(
  'adminAgentTasks/delete',
  async (taskId: string) => {
    const response = await fetch(`/api/admin/agent-tasks/${taskId}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete task');
    }
    return { taskId };
  }
);

export const retryAgentTask = createAsyncThunk(
  'adminAgentTasks/retry',
  async (taskId: string) => {
    const response = await fetch(`/api/admin/agent-tasks/${taskId}/retry`, {
      method: 'POST',
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to retry task');
    }
    return response.json();
  }
);

// Slice
const adminAgentTasksSlice = createSlice({
  name: 'adminAgentTasks',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setFilters: (state, action: PayloadAction<Partial<AdminAgentTasksFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters: (state) => {
      state.filters = initialFilters;
    },
    setSelectedTask: (state, action: PayloadAction<AgentTaskWithMessages | null>) => {
      state.selectedTask = action.payload;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.page = action.payload;
    },
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    // Fetch tasks
    builder.addCase(fetchAgentTasks.pending, (state) => {
      state.status = 'loading';
      state.error = null;
    });
    builder.addCase(fetchAgentTasks.fulfilled, (state, action) => {
      state.status = 'succeeded';
      state.tasks = action.payload.data.tasks;
      state.pagination = action.payload.data.pagination;
      state.lastUpdated = Date.now();
    });
    builder.addCase(fetchAgentTasks.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.error.message || 'Failed to fetch tasks';
    });

    // Fetch stats
    builder.addCase(fetchAgentTaskStats.pending, (state) => {
      // Don't change overall status for stats refresh
    });
    builder.addCase(fetchAgentTaskStats.fulfilled, (state, action) => {
      state.stats = action.payload.data;
      state.lastUpdated = Date.now();
    });
    builder.addCase(fetchAgentTaskStats.rejected, (state, action) => {
      state.error = action.error.message || 'Failed to fetch stats';
    });

    // Fetch details
    builder.addCase(fetchAgentTaskDetails.pending, (state) => {
      state.status = 'loading';
    });
    builder.addCase(fetchAgentTaskDetails.fulfilled, (state, action) => {
      state.status = 'succeeded';
      state.selectedTask = action.payload.data;
    });
    builder.addCase(fetchAgentTaskDetails.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.error.message || 'Failed to fetch task details';
    });

    // Cancel task
    builder.addCase(cancelAgentTask.fulfilled, (state, action) => {
      const cancelledTask = action.payload.data;
      const index = state.tasks.findIndex(t => t.id === cancelledTask.id);
      if (index !== -1) {
        state.tasks[index] = { ...state.tasks[index], ...cancelledTask };
      }
      if (state.selectedTask?.id === cancelledTask.id) {
        state.selectedTask = { ...state.selectedTask, ...cancelledTask };
      }
    });
    builder.addCase(cancelAgentTask.rejected, (state, action) => {
      state.error = action.error.message || 'Failed to cancel task';
    });

    // Delete task
    builder.addCase(deleteAgentTask.fulfilled, (state, action) => {
      state.tasks = state.tasks.filter(t => t.id !== action.payload.taskId);
      if (state.selectedTask?.id === action.payload.taskId) {
        state.selectedTask = null;
      }
      state.pagination.total -= 1;
    });
    builder.addCase(deleteAgentTask.rejected, (state, action) => {
      state.error = action.error.message || 'Failed to delete task';
    });

    // Retry task
    builder.addCase(retryAgentTask.fulfilled, (state, action) => {
      // Add new task to the beginning of the list
      state.tasks.unshift(action.payload.data);
      state.pagination.total += 1;
    });
    builder.addCase(retryAgentTask.rejected, (state, action) => {
      state.error = action.error.message || 'Failed to retry task';
    });
  },
});

export const {
  clearError,
  setFilters,
  resetFilters,
  setSelectedTask,
  setPage,
  resetState,
} = adminAgentTasksSlice.actions;

export default adminAgentTasksSlice.reducer;
